/**
 * Dataset 하나의 조사 사실 편집 상태 — 서버의 active · candidate, 편집본, 제안 · 확정 · 폐기 요청, 이력.
 *
 * 편집본은 candidate 가 있으면 candidate 에서, 없으면 active 에서 시작한다. 그 시작점(기준)과 다르면 저장하지 않은
 * 편집이다. 편집 상태는 Dataset 별로 모듈 슬롯이 든다. 상세의 섹션을 오가도(사실 섹션 ↔ 점검 화면의 요약) 같은
 * 편집본을 본다. 같은 Dataset 을 연 상세(창 · route 페이지)는 모두 이 상태를 잡고(`holdDatasetFactsEditor`), 마지막
 * 상세가 놓을 때 버린다.
 *
 * **서버가 정본이다.** 쓰기는 마지막으로 읽은 `revision` 을 `expected_revision` 으로 보낸다. 그사이 다른 창이나 MCP
 * 클라이언트가 바꿨으면 서버가 409 로 거부하고, 이 상태는 다시 받아 서버 값을 따른다(편집본은 버리고 알린다).
 * 변경 알림(`dataset_facts.changed`, 같은 Dataset) · 연결 복구(`ready`) · 화면 복귀(`reconcile`)도 다시 받고, revision 이
 * 바뀌었으면 서버 값을 따른다. 매핑과 달리 저장 전 대조 GET 이 없다 — 서버 토큰이 잠그기 때문이다.
 *
 * 보이지 않는 동안(보는 화면이 없음 · 숨긴 탭)에는 묻지 않고 "바뀌었을 수 있다" 만 기억한다. 다시 보이면 한 번 묻는다.
 * 보이는 화면은 `watch()` 로 센다. 한 화면이라도 보이면 따른다.
 *
 * Owns:
 * - 서버 상태 받기와 따르기(그 알림), 편집본과 저장하지 않은 변경, 쓰기 요청과 그 실패 문구, 이력 받기, 늦은 응답의 무시.
 * - Dataset 별 슬롯과 그 수명(잡은 상세의 수), 보이는 화면의 수.
 *
 * Does not own:
 * - 유효성 규칙 — `domain/datasetFacts.ts`. 요청과 응답 읽기 — `api/datasetFactsApi.ts`. 변경 스트림 —
 *   `@/modules/inspect/public` 의 `mappingChanges`(앱 셸이 연다). 그리기 — `page/`.
 */
import { toast } from "@carbon6/c6-frontend-design";
import { mappingChanges, type MappingChange } from "@/modules/inspect/public";
import { isHttpClientError } from "@/shared/http";
import { Holders } from "@/shared/utils/holders";
import { isPageHidden } from "@/shared/utils/pageVisibility";
import {
  confirmDatasetFacts,
  discardDatasetFactsCandidate,
  getDatasetFacts,
  getDatasetFactsHistory,
  proposeDatasetFacts,
} from "../api/datasetFactsApi";
import {
  emptyFacts,
  factIssues,
  sameFacts,
  withFact,
  type AnyFact,
  type DatasetFacts,
  type DatasetFactsRecord,
  type FactIssue,
  type FactPath,
  type FactsEvent,
} from "../domain/datasetFacts";
import {
  FACTS_CONFLICT_RELOAD_FAILED,
  FACTS_REVISION_CONFLICT_CODE,
  followFactsMessage,
  resolveFactsCommandMessage,
  type FactsFollowReason,
} from "./datasetFactsFormat";
import { resolveDatasetErrorMessage } from "./datasetFormatters";

export type FactsServerState =
  | { readonly status: "loading" }
  | { readonly status: "ready"; readonly record: DatasetFactsRecord }
  | { readonly status: "failed"; readonly message: string };

export type FactsHistoryState =
  | { readonly status: "idle" }
  | { readonly status: "loading" }
  | { readonly status: "ready"; readonly events: readonly FactsEvent[] }
  | { readonly status: "failed"; readonly message: string };

export type FactsCommand = "propose" | "confirm" | "discard";

export type DatasetFactsEditorState = {
  readonly server: FactsServerState;
  /** 편집 중인 문서. 서버를 받기 전에는 빈 문서다. */
  readonly draft: DatasetFacts;
  /** 편집본이 기준(candidate, 없으면 active)과 다른지. */
  readonly dirty: boolean;
  /** 도는 쓰기. 409 를 받으면 서버를 다시 받아 따를 때까지 남는다 — 그동안 낡은 revision 으로 다시 쓰지 않게 한다. */
  readonly pending: FactsCommand | null;
  /** 마지막 쓰기의 실패 문구. 다음 쓰기 · 편집 취소 · 서버 따르기가 지운다. */
  readonly commandError: string | null;
  /** 마지막 재대조 GET 의 실패 문구. 편집본은 그대로 둔다. */
  readonly refreshError: string | null;
  readonly history: FactsHistoryState;
};

type ChangeSource = {
  subscribe: (listener: (change: MappingChange) => void) => () => void;
  /** 스트림이 연결 중이다. 곧 올 `ready` 가 첫 받기를 부른다. */
  readonly connecting: boolean;
};

export type DatasetFactsEditorDeps = {
  fetchFacts?: typeof getDatasetFacts;
  propose?: typeof proposeDatasetFacts;
  confirm?: typeof confirmDatasetFacts;
  discard?: typeof discardDatasetFactsCandidate;
  fetchHistory?: typeof getDatasetFactsHistory;
  changes?: ChangeSource;
};

/** 편집본의 기준 — 제안이 있으면 제안, 없으면 active. */
export function baselineOf(record: DatasetFactsRecord): DatasetFacts {
  return record.candidate?.facts ?? record.active;
}

function readyRecord(state: DatasetFactsEditorState): DatasetFactsRecord | null {
  return state.server.status === "ready" ? state.server.record : null;
}

/** 제안할 수 있는지 — 기준과 다른 편집이 있고 검사를 통과한다. `issues` 는 편집본의 `factIssues` 다. */
export function canPropose(state: DatasetFactsEditorState, issues: readonly FactIssue[]): boolean {
  return (
    readyRecord(state) !== null && state.pending === null && state.dirty && issues.length === 0
  );
}

/**
 * 확정할 수 있는지 — 검사를 통과하고, 편집본이 active 와 다르거나 지울 제안이 있다. 둘 다 아니면 서버도 아무것도
 * 쓰지 않는다. `issues` 는 편집본의 `factIssues` 다.
 */
export function canConfirm(state: DatasetFactsEditorState, issues: readonly FactIssue[]): boolean {
  const record = readyRecord(state);
  return (
    record !== null &&
    state.pending === null &&
    issues.length === 0 &&
    (record.candidate !== null || !sameFacts(state.draft, record.active))
  );
}

/**
 * 제안을 폐기할 수 있는지 — 제안이 있고 저장하지 않은 편집이 없다. 편집본은 제안에서 시작했으므로 폐기하면 편집도
 * 함께 사라진다. 말없이 잃지 않게 먼저 편집을 취소하거나 제안하게 한다.
 */
export function canDiscard(state: DatasetFactsEditorState): boolean {
  const record = readyRecord(state);
  return record !== null && record.candidate !== null && state.pending === null && !state.dirty;
}

function isRevisionConflict(error: unknown): boolean {
  return isHttpClientError(error) && error.code === FACTS_REVISION_CONFLICT_CODE;
}

export class DatasetFactsEditor {
  readonly datasetUuid: string;
  private state: DatasetFactsEditorState;
  private readonly listeners = new Set<() => void>();
  private readonly deps: Required<DatasetFactsEditorDeps>;
  private unsubscribeChanges: (() => void) | null = null;
  private disposed = false;
  /** 보이는 화면이 하나라도 있다. `viewers` 가 처음 잡힐 때 켜고 마지막으로 놓일 때 끈다. */
  private active = false;
  private readonly viewers = new Holders<void>({
    first: () => this.setActive(true),
    last: () => this.setActive(false),
  });
  /** 스트림 연결을 기다리느라 미룬 첫 받기. 다음 신호가 부른다. */
  private loadDeferred = false;
  /** 마지막으로 받은 뒤 서버가 바뀌었을 수 있다. 다시 보일 때 · 쓰기가 끝날 때 묻는 근거다. */
  private stale = false;
  /** 읽기 세대. 쓰기 · 새 읽기가 올리면 그 전에 나간 읽기의 응답은 버린다. */
  private generation = 0;
  private abort: AbortController | null = null;
  private observing: Promise<void> | null = null;
  private historyGeneration = 0;

  constructor(datasetUuid: string, deps: DatasetFactsEditorDeps = {}) {
    this.datasetUuid = datasetUuid;
    this.deps = {
      fetchFacts: deps.fetchFacts ?? getDatasetFacts,
      propose: deps.propose ?? proposeDatasetFacts,
      confirm: deps.confirm ?? confirmDatasetFacts,
      discard: deps.discard ?? discardDatasetFactsCandidate,
      fetchHistory: deps.fetchHistory ?? getDatasetFactsHistory,
      changes: deps.changes ?? mappingChanges,
    };
    this.state = {
      server: { status: "loading" },
      draft: emptyFacts(),
      dirty: false,
      pending: null,
      commandError: null,
      refreshError: null,
      history: { status: "idle" },
    };
  }

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  readonly getSnapshot = (): DatasetFactsEditorState => this.state;

  /**
   * 변경 알림을 구독하고 처음 받는다. 여러 번 불러도 한 번만 한다. 스트림이 연결 중이면 받기를 미룬다 — 곧 올
   * `ready` 가 "바뀌었을 수 있다" 로 같은 GET 을 한 번 더 보내게 되기 때문이다(`MappingEditor` 와 같다).
   */
  init(): void {
    if (this.disposed || this.unsubscribeChanges !== null) return;
    this.unsubscribeChanges = this.deps.changes.subscribe((change) => this.onChange(change));
    if (this.deps.changes.connecting) {
      this.loadDeferred = true;
    } else {
      void this.load();
    }
  }

  private onChange(change: MappingChange): void {
    if (change.kind === "changed") return;
    if (change.kind === "facts_changed" && change.datasetUuid !== this.datasetUuid) return;
    // 연결을 기다리던 첫 받기 — 연결(`ready`)이든 끊김(`offline`)이든 이제 받는다. 보이는지와 관계없다.
    if (this.loadDeferred) {
      void this.load();
      return;
    }
    this.stale = true;
    // 끊긴 동안의 변경은 다시 연결(`ready`)되거나 화면에 돌아올 때(`reconcile`) 확인한다.
    if (change.kind === "offline" || !this.isWatching()) return;
    if (this.state.server.status === "failed") {
      void this.load();
    } else {
      void this.refreshServer();
    }
  }

  private isWatching(): boolean {
    return this.active && !isPageHidden();
  }

  /**
   * 화면 하나가 이 상태를 보기 시작한다. 돌려준 함수를 부르면 그만 본다. 보는 화면이 하나라도 있으면 서버 변경을
   * 따른다 — 같은 Dataset 의 창 하나를 최소화해도 보이는 다른 화면은 계속 따른다.
   */
  watch(): () => void {
    return this.viewers.hold();
  }

  /** 다시 보이면 놓친 변경이 있을 수 있을 때만 한 번 묻는다. */
  private setActive(active: boolean): void {
    this.active = active;
    if (active && this.stale && this.state.server.status === "ready") {
      void this.refreshServer();
    }
  }

  /** 서버 상태를 (다시) 받는다. 받으면 편집본을 기준으로 맞춘다. 쓰기가 도는 동안에는 읽지 않는다. */
  async load(): Promise<void> {
    if (this.disposed || this.state.pending !== null) return;
    this.loadDeferred = false;
    const generation = this.startRead();
    this.patch({ server: { status: "loading" }, commandError: null, refreshError: null });
    try {
      const record = await this.deps.fetchFacts(this.datasetUuid, this.abort?.signal);
      if (this.disposed || generation !== this.generation) return;
      this.adopt(record);
    } catch (error) {
      if (this.disposed || generation !== this.generation) return;
      this.patch({ server: { status: "failed", message: resolveDatasetErrorMessage(error) } });
      return;
    }
    // 받는 동안 알림이 왔으면 한 번 더 확인한다.
    if (this.stale && this.isWatching()) void this.refreshServer();
  }

  /**
   * 서버를 다시 받아 revision 이 바뀌었으면 따른다. 연속 알림은 진행 중인 요청 하나에 합치고, 그사이 새 알림이 오면
   * 끝난 뒤 한 번 더 묻는다.
   */
  refreshServer(): Promise<void> {
    this.stale = true;
    if (this.observing !== null) return this.observing;
    this.observing = this.observe().finally(() => {
      this.observing = null;
    });
    return this.observing;
  }

  private async observe(): Promise<void> {
    while (this.stale && !this.disposed && this.isWatching()) {
      const known = readyRecord(this.state);
      // 쓰기 중이면 그 결과가 기준을 바꾼다. 쓰기가 끝난 뒤 묻는다(`stale` 은 남긴다).
      if (known === null || this.state.pending !== null) return;
      const generation = this.startRead();
      try {
        const record = await this.deps.fetchFacts(this.datasetUuid, this.abort?.signal);
        if (this.disposed || generation !== this.generation) return;
        this.patch({ refreshError: null });
        if (record.revision !== known.revision) this.follow(record, "observed");
      } catch (error) {
        if (this.disposed || generation !== this.generation) return;
        this.stale = true;
        this.patch({ refreshError: resolveDatasetErrorMessage(error) });
        return;
      }
    }
  }

  /** 새 읽기를 시작한다. 앞선 읽기의 응답은 버린다. 받기 시작한 뒤의 알림만 `stale` 로 남는다. */
  private startRead(): number {
    this.abort?.abort();
    this.abort = new AbortController();
    this.stale = false;
    return ++this.generation;
  }

  /** 진행 중인 읽기를 버린다. 쓰기가 시작될 때 부른다 — 쓰기 전 상태의 응답이 쓰기의 결과를 덮지 않게 한다. */
  private cancelRead(): void {
    this.generation += 1;
    this.abort?.abort();
    this.abort = null;
  }

  /** 받은 상태를 기준으로 삼고 편집본을 그 값으로 맞춘다. */
  private adopt(record: DatasetFactsRecord): void {
    this.patch({
      server: { status: "ready", record },
      draft: baselineOf(record),
      dirty: false,
      commandError: null,
      refreshError: null,
    });
  }

  /** 다른 곳이 바꾼 서버 값을 따른다. 편집본은 버리고, 화면이 말없이 바뀌지 않게 알린다. */
  private follow(record: DatasetFactsRecord, reason: FactsFollowReason): void {
    const discarded = this.state.dirty;
    this.adopt(record);
    const message = followFactsMessage(reason, discarded);
    if (reason === "conflict" || discarded) {
      toast.warning(message);
    } else {
      toast.info(message);
    }
  }

  /** 칸 하나를 바꾼다. 서버를 모르거나 쓰기가 도는 동안에는 무시한다. */
  edit(path: FactPath, fact: AnyFact): void {
    const record = readyRecord(this.state);
    if (record === null || this.state.pending !== null) return;
    const draft = withFact(this.state.draft, path, fact);
    this.patch({ draft, dirty: !sameFacts(draft, baselineOf(record)) });
  }

  /** 편집본을 버리고 기준(candidate, 없으면 active)으로 돌아간다. */
  revert(): void {
    const record = readyRecord(this.state);
    if (record === null || this.state.pending !== null) return;
    this.patch({ draft: baselineOf(record), dirty: false, commandError: null });
  }

  /** 편집본을 candidate 로 올린다. active 는 그대로다. 성공하면 `true` 다. */
  propose(): Promise<boolean> {
    if (!canPropose(this.state, factIssues(this.state.draft))) return Promise.resolve(false);
    const facts = this.state.draft;
    return this.write("propose", (revision) =>
      this.deps.propose(this.datasetUuid, { expectedRevision: revision, facts }),
    );
  }

  /** 편집본을 active 로 올리고 candidate 를 지운다. 성공하면 `true` 다. */
  confirm(): Promise<boolean> {
    if (!canConfirm(this.state, factIssues(this.state.draft))) return Promise.resolve(false);
    const facts = this.state.draft;
    return this.write("confirm", (revision) =>
      this.deps.confirm(this.datasetUuid, { expectedRevision: revision, facts }),
    );
  }

  /** candidate 를 지운다. 편집본은 active 가 된다. 성공하면 `true` 다. */
  discard(): Promise<boolean> {
    if (!canDiscard(this.state)) return Promise.resolve(false);
    return this.write("discard", (revision) => this.deps.discard(this.datasetUuid, revision));
  }

  private async write(
    command: FactsCommand,
    send: (expectedRevision: number) => Promise<DatasetFactsRecord>,
  ): Promise<boolean> {
    const known = readyRecord(this.state);
    if (known === null) return false;
    this.cancelRead();
    this.patch({ pending: command, commandError: null });
    try {
      const record = await send(known.revision);
      if (this.disposed) return false;
      this.patch({ pending: null });
      this.adopt(record);
      return true;
    } catch (error) {
      if (this.disposed) return false;
      if (isRevisionConflict(error)) {
        // 따를 때까지 쓰기 상태로 둔다. 그사이 입력 · 버튼이 풀리면 낡은 revision 으로 다시 써서 409 를 또 받고,
        // 다시 받기를 끊으며, 친 값은 말없이 서버 값에 덮인다.
        await this.followConflict();
        if (this.disposed) return false;
        this.patch({ pending: null });
      } else {
        this.patch({ pending: null, commandError: resolveFactsCommandMessage(error) });
      }
      return false;
    } finally {
      // 쓰기 중에 온 알림(자기 쓰기의 알림 포함)은 끝난 뒤 한 번 확인한다. revision 이 같으면 아무것도 바꾸지 않는다.
      if (!this.disposed && this.stale && this.isWatching()) void this.refreshServer();
    }
  }

  /** 409 — 서버를 다시 받아 따른다. 다시 받지 못하면 편집본을 두고 문구를 남긴다. */
  private async followConflict(): Promise<void> {
    const generation = this.startRead();
    try {
      const record = await this.deps.fetchFacts(this.datasetUuid, this.abort?.signal);
      if (this.disposed || generation !== this.generation) return;
      this.follow(record, "conflict");
    } catch {
      if (this.disposed || generation !== this.generation) return;
      this.stale = true;
      this.patch({ commandError: FACTS_CONFLICT_RELOAD_FAILED });
    }
  }

  /** 이력을 받는다. 화면이 이력을 펼칠 때와 펼친 채 revision 이 바뀔 때 부른다. */
  async loadHistory(): Promise<void> {
    if (this.disposed) return;
    const generation = ++this.historyGeneration;
    this.patch({ history: { status: "loading" } });
    try {
      const events = await this.deps.fetchHistory(this.datasetUuid);
      if (this.disposed || generation !== this.historyGeneration) return;
      this.patch({ history: { status: "ready", events } });
    } catch (error) {
      if (this.disposed || generation !== this.historyGeneration) return;
      this.patch({ history: { status: "failed", message: resolveDatasetErrorMessage(error) } });
    }
  }

  dispose(): void {
    this.disposed = true;
    this.viewers.clear();
    this.unsubscribeChanges?.();
    this.unsubscribeChanges = null;
    this.abort?.abort();
    this.listeners.clear();
  }

  private patch(next: Partial<DatasetFactsEditorState>): void {
    this.state = { ...this.state, ...next };
    for (const listener of this.listeners) {
      listener();
    }
  }
}

// 상세가 여럿이어도 Dataset 마다 편집 상태 하나다. 그 Dataset 을 연 상세가 모두 놓으면 버린다.
const editors = new Map<string, DatasetFactsEditor>();
const holders = new Holders<string>({ last: disposeEditor });

function disposeEditor(datasetUuid: string): void {
  editors.get(datasetUuid)?.dispose();
  editors.delete(datasetUuid);
}

/** 이 Dataset 의 편집 상태를 얻는다. 없으면 만든다. 받기 시작은 화면의 effect 가 `init()` 으로 한다. */
export function acquireDatasetFactsEditor(datasetUuid: string): DatasetFactsEditor {
  const existing = editors.get(datasetUuid);
  if (existing !== undefined) return existing;
  const editor = new DatasetFactsEditor(datasetUuid);
  editors.set(datasetUuid, editor);
  return editor;
}

/**
 * 상세 하나가 이 Dataset 의 편집 상태를 잡는다. 돌려준 함수가 놓는다. 마지막 상세가 놓으면 편집 상태를 버리고, 다음
 * `acquire` 는 처음부터 받는다.
 */
export function holdDatasetFactsEditor(datasetUuid: string): () => void {
  return holders.hold(datasetUuid);
}

/**
 * 이 상세가 놓으면 잃는 사실 편집이 있는지 — 저장하지 않은 편집이 있고, 이 Dataset 을 잡은 상세가 이 하나뿐이다.
 * 다른 상세가 같은 Dataset 을 잡고 있으면 편집은 그쪽에 남는다. 창의 닫기 guard 와 route 페이지의 이동 guard 가 쓴다.
 */
export function wouldLoseDatasetFactsEdits(datasetUuid: string): boolean {
  const editor = editors.get(datasetUuid);
  return editor !== undefined && editor.getSnapshot().dirty && holders.count(datasetUuid) <= 1;
}

/** 세션 끝과 테스트 사이에 부른다. 잡은 상세와 관계없이 모두 버린다. */
export function disposeDatasetFactsEditors(): void {
  for (const datasetUuid of editors.keys()) disposeEditor(datasetUuid);
  holders.clear();
}
